import {
  Bike,
  Car,
  CheckCircle,
  Clock,
  DollarSign,
  Mail,
  Package,
  Phone,
  Shield,
  Truck,
  Users,
  Zap,
} from "lucide-react";
import { useEffect, useState } from "react";
// Import react-toastify
import { toast, ToastContainer } from "react-toastify";

import AppNavbar from "@/components/common/AppNavbar";
import Breadcrumbs from "@/components/common/Breadcrumbs";
import "react-toastify/dist/ReactToastify.css";

import { DASHBOARD_URL } from "@/config/env";
// Import the API service
import { submitDeliveryDriverApplication } from "@/services/apiService";
import { CitiesService } from "@/services/landing/citiesService";

export default function BecomeDeliveryDriver() {
  const [formData, setFormData] = useState({
    agreeToTerms: false,
    availability: "",
    city: "",
    deliveryType: "",
    email: "",
    firstName: "",
    hasLicense: false,
    hasVehicle: false,
    lastName: "",
    phone: "",
    vehicleType: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [cities, setCities] = useState<string[]>([]);
  const [citiesLoading, setCitiesLoading] = useState(true);

  useEffect(() => {
    const fetchCities = async () => {
      try {
        setCitiesLoading(true);
        const data = await CitiesService.getAll();
        // Filter active cities and extract names
        const activeCityNames = data
          .filter((city) => city.status === "active" && !city.hidden)
          .map((city) => city.name)
          .sort();
        setCities(activeCityNames);
      } catch (error) {
        console.error("Failed to load cities:", error);
        setCities([]);
        toast.error("Failed to load cities. Please try again.");
      } finally {
        setCitiesLoading(false);
      }
    };

    fetchCities();
  }, []);

  const goToDashboard = (role: string) => {
    // Open the dashboard login on port 5174 with role param
    window.location.href = `${DASHBOARD_URL}/auth/login?role=${role}`;
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, type, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]:
        type === "checkbox" ? (e.target as HTMLInputElement).checked : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    const payload = {
      agreed_terms: formData.agreeToTerms,
      availability: formData.availability,
      city: formData.city,
      email: formData.email,
      first_name: formData.firstName,
      has_license: formData.hasLicense,
      has_vehicle: formData.hasVehicle,
      last_name: formData.lastName,
      phone_number: formData.phone,
      preferred_delivery_type: formData.deliveryType,
      vehicle_type: formData.vehicleType,
    };

    try {
      const result = await submitDeliveryDriverApplication(payload);
      if (result.success) {
        toast.success("Application submitted successfully!");
        setIsSubmitted(true);
      } else {
        const errorMsg =
          result.message ||
          result.error ||
          (result.errors && typeof result.errors === "string"
            ? result.errors
            : Array.isArray(result.errors)
              ? result.errors.join(", ")
              : "Submission failed. Please try again.");
        toast.error(errorMsg);
      }
    } catch (err) {
      if (
        err &&
        typeof err === "object" &&
        "message" in err &&
        typeof (err as { message?: unknown }).message === "string"
      ) {
        toast.error((err as { message: string }).message);
      } else {
        toast.error("Network error. Please try again.");
      }
    }
    setIsSubmitting(false);
  };

  const benefits = [
    {
      description: "Competitive rates with performance bonuses",
      icon: <DollarSign className="text-success" size={32} />,
      title: "Earn R3,500+/week",
    },
    {
      description: "Choose your own working schedule",
      icon: <Clock className="text-primary" size={32} />,
      title: "Flexible Hours",
    },
    {
      description: "Comprehensive coverage for deliveries",
      icon: <Shield className="text-secondary" size={32} />,
      title: "Insurance Coverage",
    },
    {
      description: "24/7 driver support and assistance",
      icon: <Users className="text-accent" size={32} />,
      title: "Support Team",
    },
  ];

  const deliveryTypes = [
    {
      description: "Deliver meals from restaurants to customers",
      earnings: "from R10 per delivery",
      icon: <Package className="text-orange-500" size={24} />,
      title: "Food Delivery",
      type: "food",
    },
    {
      description: "Shop and deliver groceries to customers",
      earnings: "from R20 per delivery",
      icon: <Package className="text-green-500" size={24} />,
      title: "Grocery Delivery",
      type: "grocery",
    },
    {
      description: "Deliver packages and parcels across the city",
      earnings: "from R20 per delivery",
      icon: <Package className="text-blue-500" size={24} />,
      title: "Package Delivery",
      type: "package",
    },
    {
      description: "Deliver medicines and health products",
      earnings: "from R10 per delivery",
      icon: <Package className="text-red-500" size={24} />,
      title: "Pharmacy Delivery",
      type: "pharmacy",
    },
  ];

  const vehicleOptions = [
    {
      description: "Perfect for short-distance deliveries",
      icon: <Bike className="text-primary" size={32} />,
      requirements: "Must be in good condition",
      title: "Bicycle",
      type: "bicycle",
    },
    {
      description: "Fast delivery for medium distances",
      icon: <Zap className="text-secondary" size={32} />,
      requirements: "Valid motorcycle license required",
      title: "Scooter/Motorbike",
      type: "scooter",
    },
    {
      description: "Handle larger orders and longer distances",
      icon: <Car className="text-accent" size={32} />,
      requirements: "Valid driver's license required",
      title: "Car",
      type: "car",
    },
    {
      description: "Bulk deliveries and commercial orders",
      icon: <Truck className="text-warning" size={32} />,
      requirements: "Commercial license may be required",
      title: "Van/truck/Tipper",
      type: "bakkie",
    },
  ];

  const requirements = [
    "Be at least 16 years old",
    "Valid ID or work permit",
    "Own smartphone with internet access",
    "Valid driver's license (for motorized vehicles)",
    "Clean criminal background check",
    "Own delivery vehicle or bicycle",
  ];

  if (isSubmitted) {
    return (
      <>
        <AppNavbar />
        <div className="min-h-screen pt-16">
          <Breadcrumbs />
          <div className="flex items-center justify-center px-4 py-16">
            <div className="max-w-md text-center">
              <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-success/20">
                <CheckCircle className="text-success" size={40} />
              </div>
              <h1 className="mb-4 text-3xl font-bold text-success">
                Application Submitted!
              </h1>
              <p className="mb-6 text-base-content/70">
                Thank you for applying to become a Hareeng delivery driver.
                Please check your email for next steps. We will review your
                application and contact you within 2-3 business days.
              </p>
              <div className="space-y-3">
                <div className="alert alert-info">
                  <Mail size={20} />
                  <span>Check your email for onboarding details</span>
                </div>
                <div className="alert alert-warning">
                  <Phone size={20} />
                  <span>Keep your phone ready for our call</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <ToastContainer autoClose={5000} position="top-right" />
      </>
    );
  }

  return (
    <>
      <AppNavbar />
      {/* Add overflow-x-hidden to prevent horizontal scroll on mobile */}
      <div className="min-h-screen overflow-x-hidden bg-base-200 pt-16">
        <Breadcrumbs />

        {/* Hero Section - Responsive and No Overflow */}
        <section className="relative overflow-hidden">
          {/* Background Pattern */}
          <div className="pointer-events-none absolute inset-0 select-none opacity-10">
            <div className="absolute -right-20 -top-20 h-80 w-80 rounded-full bg-current"></div>
            <div className="absolute -bottom-32 -left-20 h-96 w-96 rounded-full bg-current"></div>
          </div>

          <div className="relative mx-auto w-full max-w-7xl px-4 py-8 sm:py-12 lg:py-24">
            {/* Ensure grid does not overflow */}
            <div className="grid w-full min-w-0 items-center gap-8 lg:grid-cols-2">
              {/* Left Content */}
              <div className="order-2 min-w-0 lg:order-1">
                <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-accent-content/20 px-3 py-1.5 text-xs font-medium sm:mb-6 sm:px-4 sm:py-2 sm:text-sm">
                  <Package className="h-4 w-4" />
                  <span>Join 5,000+ Delivery Partners</span>
                </div>

                <h1 className="mb-4 break-words text-3xl font-bold leading-tight sm:mb-6 sm:text-4xl md:text-5xl lg:text-6xl">
                  Earn up to <span className="text-secondary">R3,500</span> per
                  week with <span className="text-primary">delivery</span>{" "}
                  services
                </h1>

                <p className="mb-6 text-lg opacity-90 sm:mb-8 sm:text-xl lg:text-2xl">
                  Become a Hareeng delivery partner and earn money delivering
                  food, groceries, and packages!
                </p>

                {/* Benefits Grid */}
                <div className="grid gap-3 sm:grid-cols-2 sm:gap-4">
                  {benefits.map((benefit, index) => (
                    <div
                      className="flex items-start gap-3 rounded-lg bg-accent-content/10 p-3 backdrop-blur sm:items-center sm:p-4"
                      key={index}
                    >
                      <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-accent-content/20 sm:h-12 sm:w-12">
                        {benefit.icon}
                      </div>
                      <div className="min-w-0">
                        <h3 className="text-sm font-semibold sm:text-base">
                          {benefit.title}
                        </h3>
                        <p className="text-xs opacity-80 sm:text-sm">
                          {benefit.description}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Right Form */}
              <div className="order-1 mx-auto w-full min-w-0 max-w-md lg:order-2">
                <div className="card bg-base-100 text-base-content shadow-xl">
                  <div className="card-body p-4 sm:p-6 md:p-8">
                    <h2 className="card-title mb-4 text-xl sm:mb-6 sm:text-2xl">
                      Become a delivery driver
                    </h2>

                    <form
                      className="space-y-3 sm:space-y-4"
                      onSubmit={handleSubmit}
                    >
                      {/* Name Fields */}
                      <div className="grid gap-3 sm:grid-cols-2 sm:gap-4">
                        <div className="form-control">
                          <label className="label pb-1">
                            <span className="label-text text-sm font-medium sm:text-base">
                              First Name
                            </span>
                          </label>
                          <input
                            className="input input-sm input-bordered w-full sm:input-md"
                            name="firstName"
                            onChange={handleInputChange}
                            placeholder="Enter first name"
                            required
                            type="text"
                            value={formData.firstName}
                          />
                        </div>
                        <div className="form-control">
                          <label className="label pb-1">
                            <span className="label-text text-sm font-medium sm:text-base">
                              Last Name
                            </span>
                          </label>
                          <input
                            className="input input-sm input-bordered w-full sm:input-md"
                            name="lastName"
                            onChange={handleInputChange}
                            placeholder="Enter last name"
                            required
                            type="text"
                            value={formData.lastName}
                          />
                        </div>
                      </div>

                      {/* Email Field */}
                      <div className="form-control">
                        <label className="label pb-1">
                          <span className="label-text text-sm font-medium sm:text-base">
                            Email
                          </span>
                        </label>
                        <input
                          className="input input-sm input-bordered w-full sm:input-md"
                          name="email"
                          onChange={handleInputChange}
                          placeholder="Enter email address"
                          required
                          type="email"
                          value={formData.email}
                        />
                      </div>

                      {/* Phone Field - Responsive and No Overflow */}
                      <div className="form-control">
                        <label className="label pb-1">
                          <span className="label-text text-sm font-medium sm:text-base">
                            Phone number
                          </span>
                        </label>
                        <div className="flex w-full min-w-0 flex-col gap-2 sm:flex-row">
                          <select
                            className="select select-bordered select-sm w-full flex-shrink-0 sm:select-md sm:w-24"
                            name="countryCode"
                          >
                            <option>+27</option>
                          </select>
                          <input
                            className="input input-sm input-bordered w-full sm:input-md"
                            name="phone"
                            onChange={handleInputChange}
                            placeholder="Mobile number"
                            required
                            type="tel"
                            value={formData.phone}
                          />
                        </div>
                      </div>
                      {/* City Field */}
                      <div className="form-control">
                        <label className="label pb-1">
                          <span className="label-text text-sm font-medium sm:text-base">
                            City
                          </span>
                        </label>
                        <select
                          className="select select-bordered select-sm w-full sm:select-md"
                          disabled={citiesLoading}
                          name="city"
                          onChange={handleInputChange}
                          required
                          value={formData.city}
                        >
                          <option value="">
                            {citiesLoading
                              ? "Loading cities..."
                              : "Select your city"}
                          </option>
                          {cities.map((city) => (
                            <option key={city} value={city}>
                              {city}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Delivery Type */}
                      <div className="form-control">
                        <label className="label pb-1">
                          <span className="label-text text-sm font-medium sm:text-base">
                            Preferred Delivery Type
                          </span>
                        </label>
                        <select
                          className="select select-bordered select-sm w-full sm:select-md"
                          name="deliveryType"
                          onChange={handleInputChange}
                          required
                          value={formData.deliveryType}
                        >
                          <option value="">Select delivery type</option>
                          <option value="food">Food Delivery</option>
                          <option value="grocery">Grocery Delivery</option>
                          <option value="package">Package Delivery</option>
                          <option value="pharmacy">Pharmacy Delivery</option>
                          <option value="all">All Types</option>
                        </select>
                      </div>

                      {/* Vehicle Type */}
                      <div className="form-control">
                        <label className="label pb-1">
                          <span className="label-text text-sm font-medium sm:text-base">
                            Vehicle Type
                          </span>
                        </label>
                        <select
                          className="select select-bordered select-sm w-full sm:select-md"
                          name="vehicleType"
                          onChange={handleInputChange}
                          required
                          value={formData.vehicleType}
                        >
                          <option value="van">Van</option>
                          <option value="tipper">Tipper</option>
                          <option value="truck">Truck</option>
                          <option value="tow">Tower</option>
                          <option value="suv">7 Seater</option>
                          <option value="sedan">5 Seater</option>
                          <option value="scooter">Motorbike</option>
                          <option value="bicycle">Bicycle</option>
                        </select>
                      </div>
                      {/* Availability */}
                      <div className="form-control">
                        <label className="label pb-1">
                          <span className="label-text text-sm font-medium sm:text-base">
                            Availability
                          </span>
                        </label>
                        <select
                          className="select select-bordered select-sm w-full sm:select-md"
                          name="availability"
                          onChange={handleInputChange}
                          required
                          value={formData.availability}
                        >
                          <option value="">Select availability</option>
                          <option value="full-time">
                            Full-time (40+ hours/week)
                          </option>
                          <option value="part-time">
                            Part-time (20-40 hours/week)
                          </option>
                          <option value="flexible">
                            Flexible (Less than 20 hours/week)
                          </option>
                          <option value="weekends">Weekends only</option>
                          <option value="evenings">Evenings only</option>
                        </select>
                      </div>

                      {/* Checkboxes */}
                      <div className="space-y-2 sm:space-y-3">
                        <div className="form-control">
                          <label className="label cursor-pointer justify-start gap-2 py-2 sm:gap-3">
                            <input
                              checked={formData.hasVehicle}
                              className="checkbox-primary checkbox checkbox-sm sm:checkbox-md"
                              name="hasVehicle"
                              onChange={handleInputChange}
                              required
                              type="checkbox"
                            />
                            <span className="label-text text-xs sm:text-sm">
                              I own or have access to a delivery vehicle
                            </span>
                          </label>
                        </div>

                        <div className="form-control">
                          <label className="label cursor-pointer justify-start gap-2 py-2 sm:gap-3">
                            <input
                              checked={formData.hasLicense}
                              className="checkbox-primary checkbox checkbox-sm sm:checkbox-md"
                              name="hasLicense"
                              onChange={handleInputChange}
                              type="checkbox"
                            />
                            <span className="label-text text-xs sm:text-sm">
                              I have a valid drivers license (if applicable)
                            </span>
                          </label>
                        </div>

                        <div className="form-control">
                          <label className="label cursor-pointer justify-start gap-2 py-2 sm:gap-3">
                            <input
                              checked={formData.agreeToTerms}
                              className="checkbox-primary checkbox checkbox-sm sm:checkbox-md"
                              name="agreeToTerms"
                              onChange={handleInputChange}
                              required
                              type="checkbox"
                            />
                            <span className="label-text text-xs leading-relaxed sm:text-sm">
                              By registering, you agree to our{" "}
                              <a
                                className="link link-primary"
                                href="/pages/legal/terms-of-use"
                              >
                                Terms of Service
                              </a>{" "}
                              and{" "}
                              <a
                                className="link link-primary"
                                href="/pages/legal/privacy-policy"
                              >
                                Privacy Policy
                              </a>
                            </span>
                          </label>
                        </div>
                      </div>

                      {/* Submit Button */}
                      <button
                        className="btn btn-primary btn-sm w-full text-base sm:btn-md sm:text-lg"
                        disabled={isSubmitting}
                        type="submit"
                      >
                        {isSubmitting ? (
                          <>
                            <span className="loading loading-spinner loading-sm"></span>
                            Processing...
                          </>
                        ) : (
                          "Register as delivery driver"
                        )}
                      </button>
                    </form>

                    <div className="divider my-2 text-xs sm:my-4 sm:text-sm">
                      OR
                    </div>

                    <p className="text-center text-xs sm:text-sm">
                      Already have an account?{" "}
                      <a
                        className="link link-primary font-medium"
                        href="#"
                        onClick={() => goToDashboard("delivery")}
                      >
                        Log in →
                      </a>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Delivery Types */}
        <section className="py-8 sm:py-12 lg:py-16">
          <div className="mx-auto max-w-7xl px-4">
            <div className="mb-8 text-center sm:mb-12">
              <h2 className="mb-3 text-2xl font-bold text-primary sm:mb-4 sm:text-3xl">
                Delivery Options
              </h2>
              <p className="mx-auto max-w-2xl text-base text-base-content/70 sm:text-lg">
                Choose the type of deliveries that work best for you
              </p>
            </div>

            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
              {deliveryTypes.map((delivery, index) => (
                <div
                  className="card bg-base-100 shadow-lg transition-shadow hover:shadow-xl"
                  key={index}
                >
                  <div className="card-body">
                    <div className="mb-4 flex items-center justify-between">
                      {delivery.icon}
                      <div className="badge">{delivery.earnings}</div>
                    </div>
                    <h3 className="card-title text-lg">{delivery.title}</h3>
                    <p className="text-sm text-base-content/70">
                      {delivery.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Vehicle Options */}
        <section className="bg-base-100 py-8 sm:py-12 lg:py-16">
          <div className="mx-auto max-w-7xl px-4">
            <div className="mb-8 text-center sm:mb-12">
              <h2 className="mb-3 text-2xl font-bold text-primary sm:mb-4 sm:text-3xl">
                Vehicle Options
              </h2>
              <p className="mx-auto max-w-2xl text-base text-base-content/70 sm:text-lg">
                Use any of these vehicles to start delivering
              </p>
            </div>

            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
              {vehicleOptions.map((vehicle, index) => (
                <div className="card bg-base-200 shadow-lg" key={index}>
                  <div className="card-body text-center">
                    <div className="mx-auto mb-4">{vehicle.icon}</div>
                    <h3 className="card-title justify-center text-lg">
                      {vehicle.title}
                    </h3>
                    <p className="mb-2 text-sm text-base-content/70">
                      {vehicle.description}
                    </p>
                    <p className="text-xs text-base-content/60">
                      {vehicle.requirements}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Requirements */}
        <section className="py-8 sm:py-12 lg:py-16">
          <div className="mx-auto max-w-7xl px-4">
            <div className="mb-8 text-center sm:mb-12">
              <h2 className="mb-3 text-2xl font-bold text-primary sm:mb-4 sm:text-3xl">
                Requirements
              </h2>
              <p className="mx-auto max-w-2xl text-base text-base-content/70 sm:text-lg">
                Basic requirements to become a delivery driver
              </p>
            </div>

            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {requirements.map((requirement, index) => (
                <div
                  className="flex items-start gap-3 rounded-lg bg-base-100 p-4"
                  key={index}
                >
                  <CheckCircle
                    className="mt-1 flex-shrink-0 text-success"
                    size={20}
                  />
                  <span className="text-base-content/80">{requirement}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* How it Works */}
        <section className="bg-base-100 py-8 sm:py-12 lg:py-16">
          <div className="mx-auto max-w-7xl px-4">
            <div className="mb-8 text-center sm:mb-12">
              <h2 className="mb-3 text-2xl font-bold text-primary sm:mb-4 sm:text-3xl">
                How it Works
              </h2>
              <p className="mx-auto max-w-2xl text-base text-base-content/70 sm:text-lg">
                Start earning in just a few simple steps
              </p>
            </div>

            <div className="grid gap-6 sm:gap-8 md:grid-cols-4">
              <div className="text-center">
                <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-primary text-lg font-bold text-primary-content sm:mb-6 sm:h-16 sm:w-16 sm:text-xl">
                  1
                </div>
                <h3 className="mb-3 text-lg font-semibold sm:mb-4 sm:text-xl">
                  Apply Online
                </h3>
                <p className="text-sm text-base-content/70 sm:text-base">
                  Fill out the application form with your details
                </p>
              </div>

              <div className="text-center">
                <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-secondary text-lg font-bold text-secondary-content sm:mb-6 sm:h-16 sm:w-16 sm:text-xl">
                  2
                </div>
                <h3 className="mb-3 text-lg font-semibold sm:mb-4 sm:text-xl">
                  Get Verified
                </h3>
                <p className="text-sm text-base-content/70 sm:text-base">
                  Complete background check and document verification
                </p>
              </div>

              <div className="text-center">
                <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-accent text-lg font-bold text-accent-content sm:mb-6 sm:h-16 sm:w-16 sm:text-xl">
                  3
                </div>
                <h3 className="mb-3 text-lg font-semibold sm:mb-4 sm:text-xl">
                  Download App
                </h3>
                <p className="text-sm text-base-content/70 sm:text-base">
                  Get the delivery driver app and complete training
                </p>
              </div>

              <div className="text-center">
                <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-success text-lg font-bold text-success-content sm:mb-6 sm:h-16 sm:w-16 sm:text-xl">
                  4
                </div>
                <h3 className="mb-3 text-lg font-semibold sm:mb-4 sm:text-xl">
                  Start Delivering
                </h3>
                <p className="text-sm text-base-content/70 sm:text-base">
                  Go online and start accepting delivery requests
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-8 sm:py-12 lg:py-16">
          <div className="mx-auto max-w-4xl px-4 text-center">
            <h2 className="mb-3 text-2xl font-bold sm:mb-4 sm:text-3xl md:text-4xl">
              Ready to Start Delivering?
            </h2>
            <p className="mb-6 text-lg opacity-90 sm:mb-8 sm:text-xl">
              Join thousands of delivery partners already earning with Hareeng
            </p>
            <div className="flex flex-col gap-3 sm:flex-row sm:justify-center sm:gap-4">
              <button
                className="btn btn-primary btn-sm sm:btn-md lg:btn-lg"
                onClick={() => window.scrollTo({ behavior: "smooth", top: 0 })}
              >
                Apply Now
              </button>
            </div>
          </div>
        </section>
        <ToastContainer autoClose={5000} position="top-right" />
      </div>
    </>
  );
}
